import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useEmbedCommunication
 *
 * Handles postMessage communication with the embed.js loader when the app
 * is running inside an iframe. Provides:
 * - Height auto-resize reporting (ResizeObserver on document body)
 * - Parent page URL capture (sent by the loader for source tracking)
 *
 * Returns:
 *   isEmbedded  — true if running in an iframe
 *   parentUrl   — the parent page URL (if provided by the loader)
 *   parentOrigin — the origin of the parent (for security checks)
 */
export default function useEmbedCommunication() {
  const [isEmbedded] = useState(() => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  });

  const [parentUrl, setParentUrl] = useState(null);
  const [parentOrigin, setParentOrigin] = useState(null);
  const resizeObserverRef = useRef(null);

  // Report current content height to parent (debounced)
  var reportTimerRef = useRef(null);

  const reportHeight = useCallback(() => {
    if (!isEmbedded) return;

    // Debounce: clear pending report and schedule a new one
    if (reportTimerRef.current) {
      clearTimeout(reportTimerRef.current);
    }
    reportTimerRef.current = setTimeout(function () {
      var root = document.getElementById('root');
      var height = 0;
      if (root) {
        var appContainer = root.querySelector('.app-container');
        if (appContainer) {
          height = appContainer.scrollHeight;
        } else {
          height = root.scrollHeight;
        }
      }
      if (!height) {
        height = document.body.scrollHeight || document.documentElement.scrollHeight;
      }

      window.parent.postMessage({
        source: 'karlb-embed',
        type: 'resize',
        height: height
      }, '*');
    }, 100);
  }, [isEmbedded]);

  useEffect(() => {
    if (!isEmbedded) return;

    // Send "ready" message to parent on mount
    window.parent.postMessage({
      source: 'karlb-embed',
      type: 'ready'
    }, '*');

    // Report initial height after a brief delay (let React render)
    var readyTimer = setTimeout(reportHeight, 200);

    // Listen for messages from the parent (loader)
    function handleMessage(event) {
      var data = event.data;
      if (!data || data.source !== 'karlb-embed') return;

      if (data.type === 'parent-url' && data.url) {
        setParentUrl(data.url);
        setParentOrigin(event.origin);
      }
    }

    window.addEventListener('message', handleMessage);

    // Set up ResizeObserver to detect content height changes
    var root = document.getElementById('root');
    if (root && window.ResizeObserver) {
      resizeObserverRef.current = new ResizeObserver(function () {
        reportHeight();
      });
      resizeObserverRef.current.observe(root);
      // Also observe the app container if present
      var appContainer = root.querySelector('.app-container');
      if (appContainer) {
        resizeObserverRef.current.observe(appContainer);
      }
    }

    // Report height on window resize
    window.addEventListener('resize', reportHeight);

    // visualViewport fires on mobile when keyboard shows/hides or browser chrome changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', reportHeight);
      window.visualViewport.addEventListener('scroll', reportHeight);
    }

    // Cleanup
    return function () {
      clearTimeout(readyTimer);
      if (reportTimerRef.current) {
        clearTimeout(reportTimerRef.current);
      }
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('resize', reportHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', reportHeight);
        window.visualViewport.removeEventListener('scroll', reportHeight);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [isEmbedded, reportHeight]);

  return { isEmbedded, parentUrl, parentOrigin };
}
